import React, { useState, type ReactNode } from "react"
import cx from "clsx"
import {
  CheckCircleOutlineIcon,
  CloseIcon,
  ExclamationCircleOutlineIcon,
  InformationCircleOutlineIcon,
  XCircleOutlineIcon
} from "components/Icons"
import Show from "components/Show"
import { noop } from "lib/helpers"

interface BaseAlertProps {
  children?: ReactNode
  status?: `success` | `warning` | `error` | `info`
  title?: ReactNode
  className?: string
  cta?: ReactNode
  idPrefix?: string
}

export interface AlertProps extends BaseAlertProps {
  closable?: false
  onClose?: never
}

export interface ClosableAlertProps extends BaseAlertProps {
  closable: true
  onClose?: () => void
}

const AlertIcons = {
  error: XCircleOutlineIcon,
  warning: ExclamationCircleOutlineIcon,
  success: CheckCircleOutlineIcon,
  info: InformationCircleOutlineIcon
}

const Alert = ({
  children,
  status = `error`,
  title,
  cta,
  closable = false,
  onClose = noop,
  className,
  idPrefix
}: AlertProps | ClosableAlertProps) => {
  const [isOpen, setisOpen] = useState(true)
  const AlertIcon = AlertIcons[status]

  if (closable && !isOpen) {
    return null
  }

  return (
    <div
      role="alert"
      className={cx(
        `relative p-3 bg-opacity-5 border rounded-lg`,
        {
          "bg-secondary-red border-secondary-red": status === `error`,
          "bg-secondary-orange border-secondary-orange": status === `warning`,
          "bg-secondary-green border-secondary-green": status === `success`,
          "bg-black border-black": status === `info`
        },
        className
      )}
      data-testid={idPrefix ? `${idPrefix}-alert` : `alert`}
    >
      <div className="flex">
        <div className="flex-shrink-0">
          <AlertIcon className="w-5 h-5 m-0.5" />
        </div>

        <div className="ml-3">
          <Show when={title}>
            <h3 className="font-medium">{title}</h3>
          </Show>

          <p>
            {children ||
              `We were unable to complete this action, please try reloading the
            page or contact us if the problem persists.`}
          </p>

          <Show when={cta}>
            <div className="font-medium underline mt-2">{cta}</div>
          </Show>
        </div>
      </div>

      <Show when={closable}>
        <button
          aria-label="Close"
          type="button"
          className="absolute top-1 right-1 p-2 hover:opacity-75"
          onClick={() => {
            setisOpen(false)
            onClose()
          }}
        >
          <CloseIcon className="w-6 h-6" />
        </button>
      </Show>
    </div>
  )
}

export default Alert
